import Head from "next/head";
import Image from "next/image";
import { Heading, Text } from "@/components/Text";
import phone from "/public/assets/icons/phone.svg";
import email from "/public/assets/icons/email.svg";
import location from "/public/assets/icons/location.svg";
import twitter from "/public/assets/icons/twitter.svg";
import linkedin from "/public/assets/icons/linkedin.svg";
import facebook from "/public/assets/icons/facebook.svg";
import { getContactInfo } from "../../sanity/lib/getCmsData";

export async function getStaticProps() {
  const data = await getContactInfo();
  return {
    props: {
      data,
    },
    revalidate: 10,
  };
}

export default function ContactUs({ data }) {
  return (
    <>
      <Head>
        <title>Contact Us | Red Ball Drills</title>
        <meta
          name="description"
          content="Contact us at our Virginia headquarters and follow our social media accounts."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <Hero data={data} />
    </>
  );
}

function Hero({ data }) {
  return (
    <div className="relative lg:px-16">
      <section className="w-full mt-14 px-5 md:mt-16 md:px-8 xl:mt-16">
        <div className="mx-auto max-w-[1170px]">
          <div className="relative">
            <div className="pt-12 pb-24 md:pb-32 px-5 md:px-11 lg:px-0">
              <Heading as="h1" size="page" className="mb-8 md:mb-10">
                Contact Us
              </Heading>
              <div className="flex flex-col md:flex-row gap-5 lg:gap-7">
                <article className="z-10 md:w-1/2 p-9 bg-rbd-base shadow-surround rounded-xl">
                  <Heading className="mb-4">Our Office</Heading>
                  <Heading as="h3" size="contact-title" className="mb-4">
                    Virginia Headquarters
                  </Heading>
                  <ul className="flex flex-col gap-3">
                    <li className="flex gap-3">
                      <Image src={phone} alt="" />
                      <Text
                        as="a"
                        size="bodyLight"
                        href={`tel:${data?.phone}`}
                        className="mt-1"
                      >
                        {data?.phone}
                      </Text>
                    </li>
                    <li className="flex gap-3">
                      <Image src={email} alt="" />
                      <Text
                        as="a"
                        size="bodyLight"
                        href={`mailto:${data?.email}`}
                        className="mt-1"
                      >
                        {data?.email}
                      </Text>
                    </li>
                    <li className="flex gap-3 items-start">
                      <Image src={location} alt="" />
                      <address className="not-italic text-base font-light">
                        1775 Tysons Boulevard, 5th Floor
                        <br />
                        Tysons, VA 22102
                      </address>
                    </li>
                  </ul>
                </article>
                <article className="z-10 min-h-[300px] md:min-h-fit md:w-1/2 p-9 bg-rbd-base shadow-surround rounded-xl">
                  <Heading className="mb-4">Social Media</Heading>
                  <ul className="flex flex-col gap-3">
                    <li className="flex gap-3">
                      <a
                        href="https://twitter.com/redballdrills"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Follow us on Twitter"
                        className="p-1"
                      >
                        <Image src={twitter} alt="" />
                      </a>
                      <Text
                        as="a"
                        size="bodyLight"
                        href={data.socialLinks[0].url}
                        target="_blank"
                        className="mt-1"
                      >
                        {data.socialLinks[0].platform}
                      </Text>
                    </li>
                    <li className="flex gap-3">
                      <a
                        href="https://www.linkedin.com/company/red-ball-drills/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Follow us on LinkedIn"
                        className="p-1"
                      >
                        <Image src={linkedin} alt="" />
                      </a>
                      <Text
                        as="a"
                        size="bodyLight"
                        href={data.socialLinks[1].url}
                        target="_blank"
                        className="mt-1"
                      >
                        {data.socialLinks[1].platform}
                      </Text>
                    </li>
                    <li className="flex gap-3">
                      <a
                        className="shrink-0 p-1"
                        href="https://www.facebook.com/redballdrills/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Follow us on Facebook"
                      >
                        <Image src={facebook} alt="" />
                      </a>
                      <Text
                        as="a"
                        size="bodyLight"
                        href={data.socialLinks[2].url}
                        target="_blank"
                        className="mt-1"
                      >
                        {data.socialLinks[2].platform}
                      </Text>
                    </li>
                  </ul>
                </article>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
